import {Component, HostBinding, OnInit, HostListener, ViewChild, ViewContainerRef, OnDestroy} from '@angular/core';
import {MediaObserver} from '@angular/flex-layout';
import {ActivatedRoute, NavigationEnd, Router, RouterEvent} from '@angular/router';
import {MatExpansionPanel} from '@angular/material/expansion';
import {MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig} from '@angular/material/legacy-dialog';
import {select, Store} from '@ngrx/store';
import {combineLatestWith, Observable, of, share, Subscription} from 'rxjs';
import {catchError, filter, take} from 'rxjs/operators';

import {UserHelpService} from '../../service/user-help.service';
import {AnnouncementService} from '@app/views/common/dialog-announcement/announcement.service';
import {InteractiveGuideService} from '@app/core/service/interactive-guide.service';
import {ConfirmDialogComponent} from '@app/core/comp/dialog/confirm-dialog/confirm-dialog.component';
import {isVisibleContent, isMenuOpened, State, getAuthenticatedUser} from '@app/app.reducers';
import * as authAction from '@app/views/common/auth.actions';
import {CustomerService} from '@app/core/service/customer.service';
import {NavigationService} from '@app/core/comp/navigation/navigation.service';
import {User} from '@app/core/model/user';
import {AuthenticatedUserService} from '@app/core/service/authenticated-user.service';


@Component({
    selector: 'standard-page',
    templateUrl: './standard-page.component.html',
    styleUrls: ['./standard-page.component.scss']
})
export class StandardPageComponent implements OnInit, OnDestroy {

    @HostBinding('attr.layout-mode')
        layoutMode;

    @ViewChild('helpsidenav') helpSideNav:  MatExpansionPanel;

    menuOpened = false;
    menuTemporaryOpened = false;
    helpCenterAvailable: boolean;
    isVisibleContent = false;
    isCheckingClaimsForAuthentication = false;
    isMainNotificationClosed$: Observable<boolean> = this.authenticatedUserService.isMainNotificationClosed$;
    showPasswordExpirationNotification: boolean;
    passwordExpireDays: number;

    private subscriptions: Subscription[] = [];

    constructor(public media: MediaObserver,
                public dialog: MatDialog,
                public viewContainerRef: ViewContainerRef,
                private activatedRoute: ActivatedRoute,
                private router: Router,
                private store: Store<State>,
                private announcementService: AnnouncementService,
                private interactiveGuideService: InteractiveGuideService,
                private navigationService: NavigationService,
                public userHelpService: UserHelpService,
                private authenticatedUserService: AuthenticatedUserService,
    ) {
    }

    ngOnInit() {
        this.layoutMode = 'plain';

        this.checkIsShowHelpButton();

        this.subscriptions.push(this.router.events
            .pipe(filter((event: RouterEvent) => event instanceof NavigationEnd))
            .subscribe((event: RouterEvent) => {
                this.checkIsShowHelpButton();
            })
        );

        this.subscriptions.push(this.store.pipe(select(isMenuOpened))
            .subscribe((isMenuOpened: boolean) => {
                if (this.media.isActive('gt-lg') && (isMenuOpened == null)) {
                    this.menuOpened = true;
                    this.store.dispatch(new authAction.ShowMenuAction());
                } else if (this.media.isActive('lt-lg')) {
                    this.menuOpened = false;
                } else {
                    this.menuOpened = isMenuOpened;
                }
            })
        );


        this.subscriptions.push(this.store.pipe(select(isVisibleContent))
            .subscribe((isVisibleContent: boolean) => {
                this.isVisibleContent = isVisibleContent;
            })
        );

        this.subscriptions.push(
            this.announcementService.isCheckingClaimsForAuthentication
                .subscribe((value: boolean) => {
                    this.isCheckingClaimsForAuthentication = value;
                })
        );

        this.subscriptions.push(this.interactiveGuideService.openWelcomeDialog$
            .subscribe(result => {
                if (result) {
                    const config = new MatDialogConfig();
                    config.viewContainerRef = this.viewContainerRef;
                    config.autoFocus = false;

                    this.openWelcomeDialog(config);
                }
            }
            ));

        this.checkPasswordExpiration();
    }

    private checkPasswordExpiration() {
        this.subscriptions.push(
            this.store.pipe(select(getAuthenticatedUser))
                .pipe(
                    filter(user => !!user && !this.authenticatedUserService.isSysAdmin(user)),
                    take(1),
                    combineLatestWith(this.authenticatedUserService.passwordExpirationPolicy$
                        .pipe(
                            catchError(() => of(9999999999999)),
                            filter((passwordExpirationPolicy: number) => !!passwordExpirationPolicy)
                        )
                    )
                )
                .subscribe(([user, passwordExpirationPolicy]: [User, number]) => {
                    this.authenticatedUserService.setPasswordExpirationPolicy(passwordExpirationPolicy);
                    [this.showPasswordExpirationNotification, this.passwordExpireDays] = this.authenticatedUserService.checkPasswordExpiration(user);
                })
        );
    }

    private openWelcomeDialog(config): void {
        setTimeout(() => {
            const dialogRef = this.dialog.open(ConfirmDialogComponent, config);
            dialogRef.componentInstance.item = 'welcomeTour';
            dialogRef.componentInstance.header = 'Welcome to CONNECT!';
            dialogRef.componentInstance.target = 'Let us show you around. Our interactive tours will make your work with CONNECT simpler, more pleasant and more productive.';

            this.subscriptions.push(dialogRef.afterClosed()
                .subscribe((result: string) => {
                    this.interactiveGuideService.saveDecisionAndStartFirstTour(result);
                })
            );
        });
    }

    private checkIsShowHelpButton() {
        const fullPath: string = this.userHelpService.getStringFullParameterisedPath(this.activatedRoute);

        this.helpCenterAvailable = this.userHelpService.isHelpExist(fullPath);
    }

    openHelpCenter() {
        if (CustomerService.checkIsAmplifyCustomer() && this.userHelpService.isShowAmplifyHelpLink()) {
            this.userHelpService.navigateToAmplifyHelpLink();
        } else {
            this.helpSideNav.open();
            this.userHelpService.loadAnswers();
            window.document.body.classList.add('help-center-opened');
        }
    }

    closeHelpCenter() {
        this.helpSideNav.close();
        window.document.body.classList.remove('help-center-opened');
    }

    toggleMenu() {
        this.menuOpened = !this.menuOpened;
        if (!this.media.isActive('lt-lg')) {
            if (this.menuOpened) {
                this.store.dispatch(new authAction.ShowMenuAction());
            } else {
                this.store.dispatch(new authAction.HideMenuAction());
            }
        }
        this.navigationService.onNavCollapseToggled.emit(this.menuOpened);
    }

    openTemporaryMenu() {
        this.menuTemporaryOpened = true;
        this.navigationService.onNavCollapseToggled.emit(this.menuOpened || this.menuTemporaryOpened);
    }

    collapseTemporaryOpenedMenu() {
        this.menuTemporaryOpened = false;
        this.navigationService.onNavCollapseToggled.emit(this.menuOpened || this.menuTemporaryOpened);
    }

    onCloseNotification() {
        this.authenticatedUserService.changeIsMainNotificationClosed(true);
    }

    ngOnDestroy() {
        this.dialog.closeAll();
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    @HostListener('window:resize')
    onWindowResize() {
        if (this.media.isActive('lt-lg')) {
            this.menuOpened = false;
            this.navigationService.onNavCollapseToggled.emit(null);
        }
    }
}
